<template>
  <b-sidebar
    id="edit-region-sidebar"
    :visible="isEditRegionSidebarActive"
    bg-variant="white"
    sidebar-class="sidebar-lg"
    shadow
    backdrop
    no-header
    right
    @shown="populateLocalData"
    @hidden="hideSidebar"
    @change="val => $emit('update:is-edit-region-sidebar-active', val)"
  >
    <template #default="{ hide }">
      <div class="d-flex justify-content-between align-items-center content-sidebar-header px-2 py-1">
        <h5 class="mb-0">
          Edit Region
        </h5>
        <feather-icon
          class="ml-1 cursor-pointer"
          icon="XIcon"
          size="16"
          @click="hide"
        />
      </div>

      <validation-observer
ref="refFormObserver"
#default="{ invalid }"
>
        <b-form
          class="p-2"
          @submit.prevent="editRegion"
          @reset.prevent="resetForm"
        >
          <validation-provider
            #default="validationContext"
            name="Region Name"
            rules="required"
          >
            <b-form-group
              label="Region Name"
              label-for="RegionName"
            >
              <b-form-input
                id="RegionName"
                v-model="regionData.regionName"
                autofocus
                :state="getValidationState(validationContext)"
                trim
                placeholder="Region Name"
              />
              <b-form-invalid-feedback>
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <validation-provider
            #default="validationContext"
            name="Region Code"
            rules="required"
          >
            <b-form-group
              label="Region Code"
              label-for="RegionCode"
            >
              <b-form-input
                id="RegionCode"
                v-model="regionData.regionCode"
                :state="getValidationState(validationContext)"
                trim
                placeholder="Region Code"
              />
              <b-form-invalid-feedback>
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <div class="d-flex mt-2">
            <b-button
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              variant="primary"
              class="mr-2"
              type="submit"
              block
              :disabled="invalid || submitButtonDisabled"
            >
              Update
            </b-button>
            <b-button
              v-ripple.400="'rgba(186, 191, 199, 0.15)'"
              type="button"
              variant="outline-secondary"
              @click="hideSidebar"
            >
              Cancel
            </b-button>
          </div>
        </b-form>
      </validation-observer>
    </template>
  </b-sidebar>
</template>

<script>
import {
  BSidebar, BForm, BFormGroup, BFormInput, BFormInvalidFeedback, BButton,
} from 'bootstrap-vue'
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import { required } from '@validations'
import formValidation from '@core/comp-functions/forms/form-validation'
import Ripple from 'vue-ripple-directive'
import axios from 'axios'
import { ref } from '@vue/composition-api'
import moment from 'moment-timezone'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import apiEndpoints from '@/constants/apiEndpoints'

export default {
  components: {
    BSidebar,
    BForm,
    BFormGroup,
    BFormInput,
    BFormInvalidFeedback,
    BButton,
    ValidationProvider,
    ValidationObserver,
  },
  directives: {
    Ripple,
  },
  model: {
    prop: 'isEditRegionSidebarActive',
    event: 'update:is-edit-region-sidebar-active',
  },
  props: {
    isEditRegionSidebarActive: { type: Boolean, required: true },
    regionId: { type: [String, Number], required: true },
    regionName: { type: String, default: '' },
    regionCode: { type: String, default: '' },
  },
  data() {
    return {
      submitButtonDisabled: false,
      required,
    }
  },
  setup() {
    const blankRegionData = {
      regionId: '',
      regionName: '',
      regionCode: '',
    }

    const regionData = ref(JSON.parse(JSON.stringify(blankRegionData)))

    const resetRegionData = () => {
      regionData.value = JSON.parse(JSON.stringify(blankRegionData))
    }

    const { refFormObserver, getValidationState, resetForm } = formValidation(resetRegionData)

    return {
      regionData,
      refFormObserver,
      getValidationState,
      resetForm,
    }
  },
  methods: {
    hideSidebar() {
      this.$emit('update:is-edit-region-sidebar-active', false)
      this.resetForm()
    },
    populateLocalData() {
      // Maps parent props down to local reactive object to avoid prop mutation
      this.regionData.regionId = this.regionId
      this.regionData.regionName = this.regionName
      this.regionData.regionCode = this.regionCode
    },
    editRegion() {
      this.$refs.refFormObserver.validate().then(success => {
        if (!success) return

        this.submitButtonDisabled = true
        const userToken = this.$cookies.get('userToken')
        const headers = {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${userToken}`,
        }

        const payload = {
          region_id: this.regionData.regionId,
          region_name: this.regionData.regionName,
          region_code: this.regionData.regionCode,
          time: moment().format(),
        }

        axios
          .post(`${process.env.VUE_APP_API_URL}${apiEndpoints.API_EDIT_REGION}`, payload, { headers })
          .then(response => {
            this.submitButtonDisabled = false
            if (response.data.success === true) {
              this.hideSidebar()
              this.$root.$emit('refreshTable', 'refreshTable')
              this.$toast({
                component: ToastificationContent,
                position: 'top-right',
                props: {
                  title: 'Success',
                  icon: 'CheckIcon',
                  variant: 'success',
                  text: 'Your data has been modified!',
                },
              })
            } else {
              this.$toast({
                component: ToastificationContent,
                position: 'top-right',
                props: {
                  title: 'Notification',
                  icon: 'BellIcon',
                  variant: 'danger',
                  text: response.data.message || 'Something went wrong',
                },
              })
            }
          })
          .catch(error => {
            console.error(error)
            this.submitButtonDisabled = false
            this.$toast({
              component: ToastificationContent,
              position: 'top-right',
              props: {
                title: 'Error',
                icon: 'AlertTriangleIcon',
                variant: 'danger',
                text: 'Something went wrong',
              },
            })
          })
      })
    },
  },
}
</script>

<style lang="scss">
#edit-region-sidebar {
  .vs__dropdown-menu {
    max-height: 200px !important;
  }
}
</style>